/**
 * UndoRedoController - Keyboard shortcuts and history integration for Page Builder
 * 
 * Provides:
 * - Cmd/Ctrl + Z → Undo
 * - Cmd/Ctrl + Shift + Z → Redo
 * - Auto-save integration (skips save during undo/redo)
 * - Deep clone utility for immutable state management
 */

import { useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useEditorStore } from './useEditorStore';

// Deep clone utility for page state
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

/**
 * UndoRedoController component
 * 
 * Must be rendered inside EditorStoreProvider
 * Handles keyboard shortcuts and integrates with page data
 */
export const UndoRedoController = ({ 
  pageData, 
  setPageData, 
  setHasChanges,
  onSave,
}) => {
  const store = useEditorStore();
  const initializedRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  const isApplyingUndoRedoRef = useRef(false);
  const lastCommitRef = useRef(null);  // Track last committed state to prevent duplicates
  
  // Initialize present state when page data loads
  useEffect(() => {
    // Skip re-initialization if we just applied an undo/redo
    if (isApplyingUndoRedoRef.current) {
      return;
    }
    
    if (pageData?.blocks && !initializedRef.current) {
      // Deep clone the initial state
      const initialState = deepClone(pageData);
      store.setPresent(initialState);
      initializedRef.current = true;
      console.debug('[UndoRedoController] Initialized history with', pageData.blocks.length, 'blocks');
    }
  }, [pageData, store]);
  
  // Reset initialization flag when page changes
  useEffect(() => {
    return () => {
      initializedRef.current = false;
    };
  }, []);
  
  // Handle undo event from PageBuilderStudio
  const handleUndo = useCallback(() => {
    if (!store.canUndo) {
      toast.info('Nothing to undo');
      return;
    }
    
    store.undo();
  }, [store]);
  
  // Handle redo event from PageBuilderStudio
  const handleRedo = useCallback(() => {
    if (!store.canRedo) {
      toast.info('Nothing to redo');
      return;
    }
    
    store.redo();
  }, [store]);
  
  // Apply state changes from undo/redo to pageData
  useEffect(() => {
    if (store.present && store.isUndoRedo) {
      // Set flag to prevent re-initialization when pageData updates
      isApplyingUndoRedoRef.current = true;
      
      // Update page data from history
      setPageData(prev => ({
        ...prev,
        blocks: store.present.blocks,
      }));
      
      setHasChanges(true);
      
      // Determine if this was an undo or redo based on future stack
      const wasUndo = store.future.length > 0;
      toast.success(wasUndo ? 'Undone' : 'Redone', { duration: 1500 });
      
      // Clear the undo/redo flag immediately to prevent re-trigger
      store.clearUndoRedoFlag();
      
      // Reset the applying flag after a small delay (allows state to settle)
      setTimeout(() => {
        isApplyingUndoRedoRef.current = false;
      }, 100);
      
      // Trigger debounced save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        if (onSave) onSave();
      }, 1000);
    }
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [store.present, store.isUndoRedo, setPageData, setHasChanges, store, onSave]);
  
  // Listen for custom events from PageBuilderStudio
  useEffect(() => {
    const undoHandler = () => handleUndo();
    const redoHandler = () => handleRedo();
    const commitHandler = (e) => {
      const { pageState } = e.detail || {};
      if (pageState) {
        // Create a signature to detect duplicate commits
        const commitSignature = JSON.stringify(pageState.blocks?.map(b => b.id));
        
        // Skip duplicate commits (can happen with React StrictMode)
        if (lastCommitRef.current === commitSignature) {
          console.debug('[UndoRedoController] Skipping duplicate commit');
          return;
        }
        lastCommitRef.current = commitSignature;
        
        // Only commit if we have a present state to save
        if (store.present) {
          store.commit(deepClone(pageState));
          console.debug('[UndoRedoController] Committed new state, history depth:', store.past.length + 1);
        } else {
          // If no present state, just set it (first edit after load)
          store.setPresent(deepClone(pageState));
          console.debug('[UndoRedoController] Set initial present state');
        }
      }
    };
    
    window.addEventListener('editor:undo', undoHandler);
    window.addEventListener('editor:redo', redoHandler);
    window.addEventListener('editor:commit', commitHandler);
    
    return () => {
      window.removeEventListener('editor:undo', undoHandler);
      window.removeEventListener('editor:redo', redoHandler);
      window.removeEventListener('editor:commit', commitHandler);
    };
  }, [handleUndo, handleRedo, store]);
  
  // Keyboard shortcuts: Cmd/Ctrl + Z for undo, Cmd/Ctrl + Shift + Z for redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input field
      if (
        e.target.tagName === 'INPUT' || 
        e.target.tagName === 'TEXTAREA' || 
        e.target.isContentEditable
      ) {
        return;
      }
      
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      
      if (modKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        
        if (e.shiftKey) {
          // Cmd/Ctrl + Shift + Z = Redo
          handleRedo();
        } else {
          // Cmd/Ctrl + Z = Undo
          handleUndo();
        }
      }
      
      // Also support Cmd/Ctrl + Y for redo (Windows convention)
      if (modKey && e.key.toLowerCase() === 'y' && !isMac) {
        e.preventDefault();
        handleRedo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);
  
  // No UI - just handles events
  return null;
};

/**
 * Hook to commit changes to history
 * Call this when an action is completed (drag end, resize end, text blur, etc.)
 */
export const useHistoryCommit = () => {
  const store = useEditorStore();
  
  const commitToHistory = useCallback((newPageState) => {
    if (!newPageState) return;
    store.commit(deepClone(newPageState));
  }, [store]);
  
  return commitToHistory;
};

export default UndoRedoController;
